import _slicedToArray from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React, { createContext, useMemo, useContext, useState, useRef, useEffect, createElement, forwardRef } from 'react';
import { Ninetailed, selectVariant, selectHasExperienceVariants } from '@ninetailed/experience.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { logger, isBrowser } from '@ninetailed/experience.js-shared';
import { isEqual, get } from 'radash';
import { useInView } from 'react-intersection-observer';
import { isForwardRef } from 'react-is';
var NinetailedContext = /*#__PURE__*/createContext(undefined);
var NinetailedProvider = function NinetailedProvider(props) {
  var ninetailed = useMemo(function () {
    if ('ninetailed' in props) {
      return props.ninetailed;
    }
    var clientId = props.clientId,
      environment = props.environment,
      preview = props.preview,
      url = props.url,
      locale = props.locale,
      requestTimeout = props.requestTimeout,
      _props$plugins = props.plugins,
      plugins = _props$plugins === void 0 ? [] : _props$plugins,
      onLog = props.onLog,
      onError = props.onError,
      buildClientContext = props.buildClientContext,
      onInitProfileId = props.onInitProfileId,
      componentViewTrackingThreshold = props.componentViewTrackingThreshold,
      storageImpl = props.storageImpl,
      useSDKEvaluation = props.useSDKEvaluation;
    return new Ninetailed({
      clientId: clientId,
      environment: environment,
      preview: preview
    }, {
      url: url,
      plugins: plugins,
      locale: locale,
      requestTimeout: requestTimeout,
      onLog: onLog,
      onError: onError,
      buildClientContext: buildClientContext,
      onInitProfileId: onInitProfileId,
      componentViewTrackingThreshold: componentViewTrackingThreshold,
      storageImpl: storageImpl,
      useSDKEvaluation: useSDKEvaluation
    });
  }, []);
  var children = props.children;
  return /*#__PURE__*/jsx(NinetailedContext.Provider, {
    value: ninetailed,
    children: children
  });
};
var useNinetailed = function useNinetailed() {
  var ninetailed = useContext(NinetailedContext);
  if (ninetailed === undefined) {
    throw new Error('The component using the the context must be a descendant of the NinetailedProvider');
  }
  return ninetailed;
};
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var _excluded$3 = ["experiences"];
var useProfile = function useProfile() {
  var ninetailed = useNinetailed();
  var _useState = useState(ninetailed.profileState),
    _useState2 = _slicedToArray(_useState, 2),
    profileState = _useState2[0],
    setProfileState = _useState2[1];
  var profileStateRef = useRef({});

  /**
   * This effect compares the old and new profile state before updating it.
   * We use a ref to avoid an infinite loop which can happen when an empty profile state was updated with no changes.
   * This behaviour occurred as the validation handling on the error property was not set properly in the "CreateProfile" and "UpdateProfile" endpoint types.
   * Furthermore, it was also observed, that it "only" occurred when the preview plugin was used in parallel.
   */
  useEffect(function () {
    ninetailed.onProfileChange(function (profileState) {
      if (isEqual(profileState, profileStateRef.current)) {
        logger.debug('Profile State Did Not Change', profileState);
        return;
      } else {
        setProfileState(profileState);
        profileStateRef.current = profileState;
        logger.debug('Profile State Changed', profileState);
      }
    });
  }, []);
  var profileStateWithoutExperiences = _objectWithoutPropertiesLoose(profileState, _excluded$3);
  return Object.assign({}, profileStateWithoutExperiences, {
    loading: profileState.status === 'loading'
  });
};
var usePersonalize = function usePersonalize(baseline, variants) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
    holdout: -1
  };
  var profile = useProfile();
  return selectVariant(baseline, variants, profile, options);
};
var TrackHasSeenComponent = function TrackHasSeenComponent(_ref3) {
  var children = _ref3.children,
    variant = _ref3.variant,
    audience = _ref3.audience,
    isPersonalized = _ref3.isPersonalized;
  var ninetailed = useNinetailed();
  var _useInView = useInView({
      triggerOnce: true
    }),
    ref = _useInView.ref,
    inView = _useInView.inView;
  useEffect(function () {
    if (isBrowser() && inView) {
      ninetailed.trackHasSeenComponent({
        variant: variant,
        audience: audience,
        isPersonalized: isPersonalized
      });
    }
  }, [inView]);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("div", {
      ref: ref
    }), children]
  });
};
var _excluded$2 = ["component", "loadingComponent", "variants", "holdout"];
var Personalize = function Personalize(_ref) {
  var Component = _ref.component,
    LoadingComponent = _ref.loadingComponent,
    _ref$variants = _ref.variants,
    variants = _ref$variants === void 0 ? [] : _ref$variants,
    _ref$holdout = _ref.holdout,
    holdout = _ref$holdout === void 0 ? -1 : _ref$holdout,
    baseline = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  var _usePersonalize = usePersonalize(baseline, variants, {
      holdout: holdout
    }),
    loading = _usePersonalize.loading,
    variant = _usePersonalize.variant,
    isPersonalized = _usePersonalize.isPersonalized,
    audience = _usePersonalize.audience;
  var hasVariants = variants.length > 0;
  if (!hasVariants) {
    return /*#__PURE__*/jsx(Component, Object.assign({}, baseline, {
      ninetailed: {
        isPersonalized: isPersonalized,
        audience: audience
      }
    }));
  }
  if (loading) {
    if (LoadingComponent) {
      return /*#__PURE__*/jsx(LoadingComponent, {});
    }
    return /*#__PURE__*/jsx("div", {
      style: {
        opacity: 0
      },
      children: /*#__PURE__*/jsx(Component, Object.assign({}, variant, {
        ninetailed: {
          isPersonalized: isPersonalized,
          audience: audience
        }
      }))
    }, "hide");
  }
  return /*#__PURE__*/jsx(TrackHasSeenComponent, {
    variant: variant,
    audience: audience,
    isPersonalized: isPersonalized,
    children: /*#__PURE__*/createElement(Component, Object.assign({}, variant, {
      key: "".concat(audience.id, "-").concat(variant.id),
      ninetailed: {
        isPersonalized: isPersonalized,
        audience: audience
      }
    }))
  });
};
var generateSelectors = function generateSelectors(id) {
  return id.split('_').map(function (path, index, paths) {
    var dotPath = paths.slice(0, index).join('.');
    var underScorePath = paths.slice(index).join('_');
    return [dotPath, underScorePath].filter(function (path) {
      return path !== '';
    }).join('.');
  });
};
var selectValueFromProfile = function selectValueFromProfile(profile, id) {
  var selectors = generateSelectors(id);
  var matchingSelector = selectors.find(function (selector) {
    return get(profile, selector);
  });
  if (!matchingSelector) {
    return null;
  }
  return get(profile, matchingSelector);
};
var MergeTag = function MergeTag(_ref4) {
  var id = _ref4.id,
    fallback = _ref4.fallback;
  var _useProfile = useProfile(),
    loading = _useProfile.loading,
    profile = _useProfile.profile;
  if (loading || !profile) {
    return null;
  }
  var value = selectValueFromProfile(profile, id) || fallback;
  return value ? /*#__PURE__*/jsx(Fragment, {
    children: value
  }) : null;
};
var useExperience = function useExperience(_ref5) {
  var baseline = _ref5.baseline,
    experiences = _ref5.experiences;
  var ninetailed = useNinetailed();
  var hasVariants = experiences.map(function (experience) {
    return selectHasExperienceVariants(experience, baseline);
  }).reduce(function (acc, curr) {
    return acc || curr;
  }, false);
  var _useState3 = useState({
      hasVariants: hasVariants,
      baseline: baseline,
      error: null,
      loading: true,
      status: 'loading',
      experience: null,
      variant: baseline,
      variantIndex: 0,
      audience: null,
      isPersonalized: false,
      profile: null
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    experience = _useState4[0],
    setExperience = _useState4[1];
  useEffect(function () {
    return ninetailed.onSelectVariant({
      baseline: baseline,
      experiences: experiences
    }, function (state) {
      setExperience(state);
    });
  }, [JSON.stringify(baseline), JSON.stringify(experiences)]);
  return experience;
};
var ComponentMarker = /*#__PURE__*/forwardRef(function (_, ref) {
  var _useNinetailed = useNinetailed(),
    logger = _useNinetailed.logger;
  var markerRef = useRef(null);
  useEffect(function () {
    var _markerRef$current;
    /*
    Due to React's limitation on setting !important styles during rendering, we set the display property on the DOM element directly.
    See: https://github.com/facebook/react/issues/1881
    */
    (_markerRef$current = markerRef.current) == null || _markerRef$current.style.setProperty('display', 'none', 'important');
  }, []);
  useEffect(function () {
    logger.debug('Using fallback mechanism to detect when experiences are seen. This can lead to inaccurate results. Consider using a forwardRef instead. See: https://docs.ninetailed.io/for-developers/experience-sdk/rendering-experiences#tracking-impressions-of-experiences');
  }, [logger]);
  useEffect(function () {
    if (markerRef.current) {
      var observableElement = getObservableElement(markerRef.current);
      if (ref) {
        if (typeof ref === 'function') {
          ref(observableElement);
        } else {
          ref.current = observableElement;
        }
      }
    }
  }, []);
  return /*#__PURE__*/jsx("div", {
    className: "nt-cmp-marker",
    style: {
      display: 'none'
    },
    ref: markerRef
  });
});
var getObservableSibling = function getObservableSibling(element) {
  var nextSibling = element.nextElementSibling;
  if (!nextSibling) {
    return null;
  }
  var nextSiblingStyle = getComputedStyle(nextSibling);

  // Elements with display: none are not observable by the IntersectionObserver
  if (nextSiblingStyle.display !== 'none') {
    return nextSibling;
  }
  return getObservableSibling(nextSibling);
};
var getObservableElement = function getObservableElement(element) {
  var observableSibling = getObservableSibling(element);
  if (observableSibling) {
    return observableSibling;
  }
  var parentElement = element.parentElement;
  if (!parentElement) {
    return null;
  }
  return getObservableElement(parentElement);
};
var _excluded$1 = ["component", "unhideAfterMs", "passthroughProps"],
  _excluded2 = ["experiences", "component", "loadingComponent", "passthroughProps"];
var DefaultExperienceLoadingComponent = function DefaultExperienceLoadingComponent(_ref) {
  var Component = _ref.component,
    _ref$unhideAfterMs = _ref.unhideAfterMs,
    unhideAfterMs = _ref$unhideAfterMs === void 0 ? 5000 : _ref$unhideAfterMs,
    passthroughProps = _ref.passthroughProps,
    baseline = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  var _useNinetailed2 = useNinetailed(),
    logger = _useNinetailed2.logger;
  var _useState5 = useState(true),
    _useState6 = _slicedToArray(_useState5, 2),
    hidden = _useState6[0],
    setHidden = _useState6[1];
  useEffect(function () {
    var timer = setTimeout(function () {
      setHidden(false);
      logger.error(new Error("The experience was still in loading state after ".concat(unhideAfterMs, "ms. That happens when no events are sent to the Ninetailed API. The baseline is now shown instead.")));
    }, unhideAfterMs);
    return function () {
      clearTimeout(timer);
    };
  }, []);
  if (hidden) {
    return /*#__PURE__*/jsx("div", {
      style: {
        opacity: 0
      },
      children: /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, baseline, {
        ninetailed: {
          isPersonalized: false,
          audience: {
            id: 'baseline'
          }
        }
      }))
    }, "hide");
  }
  return /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, baseline, {
    ninetailed: {
      isPersonalized: false,
      audience: {
        id: 'baseline'
      }
    }
  }));
};
var Experience = function Experience(_ref2) {
  var experiences = _ref2.experiences,
    Component = _ref2.component,
    _ref2$loadingComponen = _ref2.loadingComponent,
    LoadingComponent = _ref2$loadingComponen === void 0 ? DefaultExperienceLoadingComponent : _ref2$loadingComponen,
    passthroughProps = _ref2.passthroughProps,
    baseline = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  var _useNinetailed3 = useNinetailed(),
    observeElement = _useNinetailed3.observeElement,
    unobserveElement = _useNinetailed3.unobserveElement,
    logger = _useNinetailed3.logger;
  var _useExperience = useExperience({
      baseline: baseline,
      experiences: experiences
    }),
    status = _useExperience.status,
    hasVariants = _useExperience.hasVariants,
    experience = _useExperience.experience,
    variant = _useExperience.variant,
    variantIndex = _useExperience.variantIndex,
    audience = _useExperience.audience,
    isPersonalized = _useExperience.isPersonalized;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  var isComponentForwardRef = isForwardRef( /*#__PURE__*/jsx(Component, {}));
  var componentRef = useRef(null);
  var isVariantHidden = 'hidden' in variant && variant.hidden;
  useEffect(function () {
    var componentElement = componentRef.current;
    if (componentElement && !(componentElement instanceof Element)) {
      var isObject = typeof componentElement === 'object' && componentElement !== null;
      var constructorName = isObject ? componentElement.constructor.name : '';
      var isConstructorNameNotObject = constructorName && constructorName !== 'Object';
      logger.warn("The component ref being in Experience is an invalid element. Expected an Element but got ".concat(typeof componentElement).concat(isConstructorNameNotObject ? " of type ".concat(constructorName) : '', ". This component won't be observed."));
      return function () {
        // noop
      };
    }
    if (componentElement) {
      observeElement({
        element: componentElement,
        experience: experience,
        audience: audience,
        variant: isVariantHidden ? Object.assign({}, variant, {
          id: "".concat(baseline.id, "-hidden")
        }) : variant,
        variantIndex: variantIndex
      });
      return function () {
        if (componentElement) {
          unobserveElement(componentElement);
        }
      };
    }
    return function () {
      // noop
    };
  }, [observeElement, unobserveElement, experience, baseline, variant, variantIndex, audience, isVariantHidden]);
  if (!hasVariants) {
    return /*#__PURE__*/jsxs(Fragment, {
      children: [!isComponentForwardRef && /*#__PURE__*/jsx(ComponentMarker, {
        ref: componentRef
      }, "marker-no-variants-".concat((experience == null ? void 0 : experience.id) || 'baseline', "-").concat(variant.id)), /*#__PURE__*/createElement(Component, Object.assign({}, passthroughProps, baseline, {
        key: baseline.id
      }, isComponentForwardRef ? {
        ref: componentRef
      } : {}))]
    });
  }
  if (status === 'loading') {
    return /*#__PURE__*/createElement(LoadingComponent, Object.assign({}, baseline, {
      key: baseline.id,
      passthroughProps: passthroughProps,
      experiences: experiences,
      component: Component
    }));
  }
  if (isVariantHidden) {
    return /*#__PURE__*/jsx(ComponentMarker, {
      ref: componentRef
    }, "marker-hidden-".concat((experience == null ? void 0 : experience.id) || 'baseline', "-").concat(variant.id));
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [!isComponentForwardRef && /*#__PURE__*/jsx(ComponentMarker, {
      ref: componentRef
    }, "marker-".concat((experience == null ? void 0 : experience.id) || 'baseline', "-").concat(variant.id)), /*#__PURE__*/createElement(Component, Object.assign({}, Object.assign({}, passthroughProps, variant), {
      key: "".concat((experience == null ? void 0 : experience.id) || 'baseline', "-").concat(variant.id),
      ninetailed: {
        isPersonalized: isPersonalized,
        audience: {
          id: (audience == null ? void 0 : audience.id) || 'all visitors'
        }
      }
    }, isComponentForwardRef ? {
      ref: componentRef
    } : {}))]
  });
};
var _excluded = ["experiences", "component", "passthroughProps"];
var ESRContext = /*#__PURE__*/React.createContext(undefined);
var ESRProvider = function ESRProvider(_ref6) {
  var experienceVariantsMap = _ref6.experienceVariantsMap,
    children = _ref6.children;
  return /*#__PURE__*/jsx(ESRContext.Provider, {
    value: {
      experienceVariantsMap: experienceVariantsMap
    },
    children: children
  });
};
var useESR = function useESR() {
  var context = React.useContext(ESRContext);
  if (context === undefined) {
    throw new Error('The component using the the context must be a descendant of the ESRProvider');
  }
  return {
    experienceVariantsMap: context.experienceVariantsMap
  };
};
var ESRLoadingComponent = function ESRLoadingComponent(_ref) {
  var experiences = _ref.experiences,
    Component = _ref.component,
    passthroughProps = _ref.passthroughProps,
    baseline = _objectWithoutPropertiesLoose(_ref, _excluded);
  var _useESR = useESR(),
    experienceVariantsMap = _useESR.experienceVariantsMap;
  var experience = experiences.find(function (experience) {
    return Object.prototype.hasOwnProperty.call(experienceVariantsMap, experience.id);
  });
  if (!experience) {
    return /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, baseline, {
      ninetailed: {
        isPersonalized: false,
        audience: {
          id: 'baseline'
        }
      }
    }));
  }
  var component = experience.components.find(function (component) {
    return component.baseline.id === baseline.id;
  });
  if (!component) {
    return /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, baseline, {
      ninetailed: {
        isPersonalized: false,
        audience: {
          id: 'baseline'
        }
      }
    }));
  }
  if (experienceVariantsMap[experience.id] === 0) {
    return /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, baseline, {
      ninetailed: {
        isPersonalized: false,
        audience: {
          id: 'baseline'
        }
      }
    }));
  }
  var variant = component.variants[experienceVariantsMap[experience.id] - 1];
  if (!variant) {
    return /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, baseline, {
      ninetailed: {
        isPersonalized: false,
        audience: {
          id: 'baseline'
        }
      }
    }));
  }
  return /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, variant, {
    ninetailed: {
      isPersonalized: false,
      audience: {
        id: 'baseline'
      }
    }
  }));
};
export { DefaultExperienceLoadingComponent, ESRLoadingComponent, ESRProvider, Experience, MergeTag, NinetailedProvider, Personalize, TrackHasSeenComponent, useExperience, useNinetailed, usePersonalize, useProfile };